import { useState } from 'react'
import { motion } from 'framer-motion'
import { LogIn, Eye, EyeOff } from 'lucide-react'
import { InnerHero } from '@/components/sections'
import { Prefooter, Footer } from '@/components/layout'
import { LocalizedLink } from '@/components/LocalizedLink'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!email.trim() || !password.trim()) {
      setError('Inserisci email e password.')
      return
    }
    // Placeholder: authentication logic will be wired here
  }

  return (
    <main className="bg-cream">
      <InnerHero
        eyebrow="Area riservata"
        title="Benvenuta"
        titleItalic="#NashiLover."
        body="Accedi al tuo account per consultare i punti, i coupon e i vantaggi del tuo programma fedeltà."
        breadcrumbs={[
          { label: 'Home', href: '/it' },
          { label: 'Login' },
        ]}
      />

      {/* ───────── Login form ───────── */}
      <section className="relative bg-cream py-16 md:py-24 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-12 right-[-3%] select-none font-display italic text-argan/[0.05]"
          style={{ fontSize: 'clamp(10rem, 22vw, 22rem)', lineHeight: 0.85 }}
        >
          n
        </div>

        <div className="relative nashi-container">
          <div className="max-w-md mx-auto">
            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9 }}
              className="space-y-8"
              noValidate
            >
              {/* Email */}
              <div>
                <label
                  htmlFor="login-email"
                  className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3"
                >
                  Email <span className="text-argan-deep">*</span>
                </label>
                <input
                  id="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="la-tua@email.com"
                  className="nashi-input"
                  autoComplete="email"
                />
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="login-password"
                  className="block text-[10px] uppercase tracking-[0.32em] text-bruno-muted mb-3"
                >
                  Password <span className="text-argan-deep">*</span>
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="nashi-input pr-12"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-bruno-muted hover:text-bruno transition-colors"
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" strokeWidth={1.5} />
                    ) : (
                      <Eye className="h-4 w-4" strokeWidth={1.5} />
                    )}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3"
                >
                  {error}
                </motion.p>
              )}

              {/* Actions */}
              <div>
                <button
                  type="submit"
                  className="group inline-flex items-center justify-center gap-3 bg-bruno text-cream font-medium uppercase tracking-[0.28em] text-[11px] px-10 py-4 hover:bg-argan-deep transition-colors"
                >
                  Accedi
                  <LogIn
                    className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5"
                    strokeWidth={1.75}
                  />
                </button>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4 py-2">
                <span className="h-px flex-1 bg-bruno/10" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                  Oppure
                </span>
                <span className="h-px flex-1 bg-bruno/10" />
              </div>

              {/* CTA to club */}
              <div className="text-center">
                <p className="text-bruno-soft text-[15px] leading-relaxed">
                  Non sei ancora iscritta al NashiLovers Club?
                </p>
                <LocalizedLink
                  to="nashiLoversClub"
                  className="nashi-cta mt-4 inline-flex"
                >
                  Scopri il programma fedeltà
                </LocalizedLink>
              </div>
            </motion.form>
          </div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
